import { Background, Main, Text } from 'styles/themeColors';
import { ConsignmentDetails } from 'data/Consignment';
import React, { Fragment, type JSX } from 'react';
import { CONSIGNMENT_CREDIT_PASSED, CONSIGNMENT_CREDIT_OVERRIDE } from 'constants/consignmentCreditStatuses';
import styled, { css } from 'styled-components';
import Icon from 'view/Icon';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { capitalize, capitalizeFirstLetter, currencySign } from 'utils/formatters';
import PickupContact from 'data/PickupContact';
import { DOW_JONES_BLOCKED } from 'constants/dowJonesStatuses';
import FeatureFlag from 'data/FeatureFlag';
import {
  CHECKING_CREDIT,
  ORDER_SENT_TO_OEM,
  READY,
  ON_HOLD,
  PROCESSING,
  MANUFACTURING,
  PACKING,
  SHIPPED_READY_FOR_PICKUP,
  READY_TO_SHIP,
  READY_FOR_PICKUP,
  DELIVERED_PICKED_UP,
  DELAYED,
  AWAITS_SHIPPING,
  AWAITS_SHIPPING_APPROVAL,
  DOW_JONES_FAILED,
  SHIPPED,
  PICKED_UP,
  RENT_IN_PROGRESS,
  RENT_RETURN_PENDING,
  RENT_COMPLETED,
  MODIFIED_BY_CUSTOMER,
  MODIFIED_BY_VENDOR,
  RENT_UPDATED,
  NEW,
  SPLIT_CONSIGNMENT,
  SUBSCRIPTION_ACTIVE,
  SUBSCRIPTION_RENEWAL,
  AFFILIATE_PROGRAM_ACTIVE,
  AWAITS_EDIT_APPROVAL
} from 'constants/consignmentStates';
import { theme } from 'OnboardingComponents/OnboardingPageTheme';
import { PRODUCT_VARIANT_TYPE } from 'constants/productVariantImport';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { toUTCDay } from 'utils/date';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { FontWeight } from 'lib/styles';
import { Space } from 'styles/themeSpaces';
import { FontSize } from 'styles/themeSizes';
import { CONSIGNMENT_TYPES, PRICE_PENDING_PREVIOUS_STATES } from 'constants/order';
import { CREDIT_DESCRIPTIONS, CREDIT_SOURCE_ACTIVITY_STATUS, CREDIT_SOURCES } from 'constants/credit';

const StyledPendingIcon = styled(Icon) <any>`
  margin-left: 5px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TableData = styled.td<any>`
  text-align: center;
  vertical-align: top;

  &:first-child:not(:only-child):not([rowspan]) {
    text-align: left;
    font-weight: ${FontWeight.bold};
  }

  &:first-child:not(:only-child)[rowspan] {
    text-align: left;
  }

  &:last-child:not(:only-child)[rowspan] {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
    font-weight: ${FontWeight.bold};
  }

  ${({ color }) => color && css`
    color: ${color};
  `}

  ${({ textDecoration }) => textDecoration && css`
    text-decoration: ${textDecoration};
  `}

  ${({ fontSize }) => fontSize && css`
    font-size: ${fontSize};
  `}

  ${({ fontWeight }) => fontWeight && css`
    font-weight: ${fontWeight};
  `}

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  min-height: 18px;
  width: 234px;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

  ${({ deleted }) => deleted && css`
  opacity: 0.5;
`}
`;

export const retrieveConsignmentCreditCheckStatusText = (consignment: ConsignmentDetails): string => {
  if (consignment.state === CHECKING_CREDIT) return 'Pending';

  const status = retrieveConsignmentCreditStatus(consignment);

  return capitalize(status);
};

export const retrieveConsignmentCreditCheckStatusIcon = (consignment: ConsignmentDetails): JSX.Element => {
  if (consignment.state === CHECKING_CREDIT) return <StyledPendingIcon name="PendingCredit" fontSize='small' />;

  const status = retrieveConsignmentCreditStatus(consignment);

  return [CONSIGNMENT_CREDIT_PASSED, CONSIGNMENT_CREDIT_OVERRIDE].includes(status)
    ? <CheckCircle
      fontSize='small'
      htmlColor={Main.success}
    />
    : <Cancel
      fontSize='small'
      htmlColor={Main.error}
    />;
};

export const retrieveConsignmentCreditStatus = (consignment: ConsignmentDetails) => {
  return (consignment.consignmentCredit && consignment.consignmentCredit.status);
};

export const parseRecipientName = (pickupContact: PickupContact | any) => {
  return `${pickupContact?.firstName || ''} ${pickupContact?.lastName || ''}`;
}

export const stringifyPickupContact = (pickupContact: PickupContact) => {
  let fields = [];
  fields.push(`${pickupContact?.firstName || ''} ${pickupContact?.lastName || ''}`)
  fields.push(pickupContact?.phone)
  fields.push(pickupContact?.email)

  fields = fields.filter(x => x?.trim());
  return fields.join(", ");
}

/**
 * Recalculate Rental Start and End Dates On Change of Pickup or Shipping Date
 * @param deliveryDateValue New pickup or shipping date
 * @param rentalStartDate Old Rental Start Date
 * @param rentalEndDate Ols Rental End Date
 * @returns New Rental Start and End Dates
 */
export const recalculateRentalDates = (deliveryDateValue: any, rentalStartDate: any, rentalEndDate: any) => {
  const newShippingDate = new Date(deliveryDateValue).toUTCString();

  const oldStart = new Date(rentalStartDate);
  const oldEnd = new Date(rentalEndDate);
  const rentalPeriod = oldEnd.getTime() - oldStart.getTime();

  const newEndDate = new Date(newShippingDate);
  newEndDate.setTime(newEndDate.getTime() + rentalPeriod);

  return {
    newRentalStartDate: newShippingDate,
    newRentalEndDate: newEndDate.toUTCString()
  }
}

/**
 * Returns the display state lable of the consignment
 * @param consignmentState Consignment State
 * @param isVendor 
 * @returns 
 */
export const getMappedDisplayState = (consignmentState: string, isVendor: boolean, dowJonesStatus: string, featureFlags: FeatureFlag) => {
  const isDowJonesBlocked =
    (dowJonesStatus === DOW_JONES_BLOCKED ||
      consignmentState === DOW_JONES_FAILED) &&
    featureFlags.dowJones &&
    Boolean(featureFlags.dowJones.enabled);

  const stateMappings: Record<string, string> = {
    CHECKING_CREDIT: 'Checking Credit',
    READY: isVendor ? (isDowJonesBlocked ? 'Compliance Check' : 'New') : (isDowJonesBlocked ? 'Compliance Fail' : 'New'),
    ORDER_SENT_TO_OEM: isVendor ? (isDowJonesBlocked ? 'Compliance Check' : 'New') : (isDowJonesBlocked ? 'Compliance Fail' : 'New'),
    NEW: isVendor ? (isDowJonesBlocked ? 'Compliance Check' : 'New') : (isDowJonesBlocked ? 'Compliance Fail' : 'New'),
    DOW_JONES_FAILED: isVendor ? 'Compliance Check' : 'Compliance Fail',
    AWAITS_SHIPPING: 'Shipping cost required',
    AWAITS_SHIPPING_APPROVAL: 'Shipping cost approval',
    CANCELED: 'Canceled',
    CANCELING: isVendor ? 'Canceled' : 'Canceling',
    ON_HOLD: 'On Hold',
    PROCESSING: 'Processing',
    SPLIT_CONSIGNMENT: 'Splitting',
    MANUFACTURING: 'Manufacturing',
    PACKING: 'Preparing',
    SHIPPED_READY_FOR_PICKUP: 'Shipped / Ready for Pickup',
    READY_TO_SHIP: 'Ready to ship',
    READY_FOR_PICKUP: 'Ready for Pickup',
    DELIVERED_PICKED_UP: 'Delivered / Picked up',
    SHIPPED: 'Shipped',
    PICKED_UP: 'Picked up',
    RENT_IN_PROGRESS: 'Rental in progress',
    RENT_RETURN_PENDING: 'Rental return pending',
    RENT_COMPLETED: 'Rental completed',
    DELAYED: 'Delayed',
    MODIFIED_BY_CUSTOMER: 'Rent modified by customer',
    MODIFIED_BY_VENDOR: 'Rent modified by vendor',
    RENT_UPDATED: 'Rent Updated',
    TICKET_IN_PROGRESS: 'Ticket in progress',
    IN_STOCK: 'In Stock',
    CANCELLATION_IN_PROGRESS: 'Cancelation in progress',
    CANCELATION_FAILED: 'Cancelation failed',
    SUBSCRIPTION_ACTIVE: 'Subscription active',
    SUBSCRIPTION_RENEWAL: 'Subscription renewal',
    AFFILIATE_PROGRAM_ACTIVE: 'Affiliate program active',
    AWAITS_EDIT_APPROVAL: 'Approval pending'
  };

  return { displayState: stateMappings[consignmentState] || '' };
};

const successStates = new Set([NEW, READY, ORDER_SENT_TO_OEM, MANUFACTURING, PROCESSING, READY_TO_SHIP, READY_FOR_PICKUP, SHIPPED_READY_FOR_PICKUP, SHIPPED, PICKED_UP, DELIVERED_PICKED_UP, PACKING, AWAITS_SHIPPING, RENT_IN_PROGRESS, RENT_COMPLETED, SUBSCRIPTION_ACTIVE, AFFILIATE_PROGRAM_ACTIVE]);
const warningStates = new Set([ON_HOLD, DELAYED, RENT_RETURN_PENDING, MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR, RENT_UPDATED, SUBSCRIPTION_RENEWAL]);
const navyStates = new Set([AWAITS_SHIPPING_APPROVAL, CHECKING_CREDIT, SPLIT_CONSIGNMENT, AWAITS_EDIT_APPROVAL]);

export const getPillTheme = (consignmentState: string, dowJonesStatus: string = '', isVendor: boolean = false) => successStates.has(consignmentState) && dowJonesStatus !== DOW_JONES_BLOCKED
  ? 'success'
  : warningStates.has(consignmentState) || (!isVendor && dowJonesStatus === DOW_JONES_BLOCKED)
    ? 'warning'
    : navyStates.has(consignmentState) || (isVendor && dowJonesStatus === DOW_JONES_BLOCKED)
      ? Background.navy
      : 'danger';

/**
 * Compares current and previous values in an array.
 * @param {Array<Object>} array - The array of objects for which comparison is to be done.
 * @param {Array<string>} fieldsToCompare - The fields to compare in each object.
 * @returns {Array<Object>} - Returns an array with current and previous values for each specified field.
 */
export const mapCurrentAndPreviousValuesInArray = (array: any[], fieldsToCompare: string[]) => {

  // Map the array and compare the current and previous values
  return array.map((item, index) => {
    const previousItem = index === 0 ? item : array[index - 1];

    fieldsToCompare.forEach(field => {
      item[`current${capitalizeFirstLetter(field)}`] = item[field];
      item[`previous${capitalizeFirstLetter(field)}`] = previousItem[field];
    });

    return item;
  });

}

/**
 * Collapses runs of consecutive history rows that share a toConsignmentStatus.
 * Every row carrying a message is kept; when a run has none, only its first row survives.
 * @param {Array<Object>} rows - History rows sorted oldest first.
 * @returns {Array<Object>} - Returns the rows with same-status duplicates collapsed.
 */
export const collapseSameStatusHistoryRuns = <T extends { toConsignmentStatus: string; message?: string }>(rows: T[]): T[] => {
  const collapsed: T[] = [];
  let start = 0;

  while (start < rows.length) {
    let end = start + 1;

    while (end < rows.length && rows[end].toConsignmentStatus === rows[end - 1].toConsignmentStatus) {
      end += 1;
    }

    const run = rows.slice(start, end);
    const carryingMessage = run.filter(entry => entry.message);

    collapsed.push(...(carryingMessage.length ? carryingMessage : [run[0]]));
    start = end;
  }

  return collapsed;
}

type HistoryRowStates = { fromConsignmentStatus: string; toConsignmentStatus: string; message?: string };

/**
 * Whether a history row is the buyer's decline of an edited order. DS sends the decline as
 * ORDER_SENT_TO_OEM → ORDER_SENT_TO_OEM, and SellerHub puts the decline message on that row,
 * so only a row carrying a message counts. The order history filter and Order progress both
 * keep this row; they must agree, or one shows what the other drops.
 */
export const isBuyerDecline = (item?: HistoryRowStates) =>
  !!item?.message
  && item.fromConsignmentStatus === ORDER_SENT_TO_OEM
  && item.toConsignmentStatus === ORDER_SENT_TO_OEM;

/**
 * Whether a history row records an edited order going back to New with a message: a withdraw
 * (AWAITS_EDIT_APPROVAL → ORDER_SENT_TO_OEM) or the buyer's decline (see isBuyerDecline).
 * Order history hides ORDER_SENT_TO_OEM rows except these; the message is the condition.
 */
export const isWithdrawOrBuyerDecline = (item?: HistoryRowStates) =>
  (!!item?.message
    && item.fromConsignmentStatus === AWAITS_EDIT_APPROVAL
    && item.toConsignmentStatus === ORDER_SENT_TO_OEM)
  || isBuyerDecline(item);

/**
 * Maps consignment type to tag color.
 * @param {string} type - The type of consignment.
 */
export const mapConsignmentTypeTag = (type: string) => {
  switch (type) {
    case CONSIGNMENT_TYPES.PURCHASE:
      return theme.blue;

    case CONSIGNMENT_TYPES.RENT:
    case CONSIGNMENT_TYPES.SUBSCRIPTION:
    case CONSIGNMENT_TYPES.AFFILIATE_PROGRAM:
      return Main.success;

    default:
      return theme.blue;
  }
}

/**
 * Maps consignment type to tag label.
 * @param {string} type - The type of consignment.
 */
export const mapConsignmentTypeLabel = (type: string) => {
  switch (type) {
    case CONSIGNMENT_TYPES.PURCHASE:
      return PRODUCT_VARIANT_TYPE.PURCHASE;

    case CONSIGNMENT_TYPES.RENT:
      return PRODUCT_VARIANT_TYPE.RENT;

    case CONSIGNMENT_TYPES.SUBSCRIPTION:
      return PRODUCT_VARIANT_TYPE.SUBSCRIPTION;
    
    case CONSIGNMENT_TYPES.AFFILIATE_PROGRAM:
      return PRODUCT_VARIANT_TYPE.AFFILIATE_PROGRAM;

    default:
      return PRODUCT_VARIANT_TYPE.PURCHASE;
  }
}

export const parseConsignmentId = (consignmentId?: string) => {
  if (!consignmentId) return '';
  return consignmentId?.replace('cons', '');
}

export const serialNumberValidator = (value: string) => {
  let errorMessage = '';
  if (!value) {
    return errorMessage
  }

  if (value.length < 8 || value.length > 20) {
    errorMessage += 'Serial number must be between 8 and 20 characters. ';
  }

  const regex = /[^a-zA-Z0-9\s\-/]/;
  if (regex.test(value)) {
    errorMessage += 'Serial number can only contain letters, numbers, dashes (-) and hyphens (/).'
  }

  return errorMessage;
}

export const productAddonQuantityValidator = (value: any) => {
  if (!value || value === '0') {
    return 'Quantity cannot be empty.'
  }

  if (value < 0) {
    return 'Quantity must be a postive integer.'
  }

  if (value > 9999) {
    return 'Quantity cannot exceed 9999.'
  }

  return '';
}

export const productAddonPlannedUsageValidator = (value: any) => {
  if (!value) {
    return 'Planned usage cannot be empty.'
  }

  if (value < 0) {
    return 'Planned usage must be a postive number.'
  }

  return '';
}

export const addonsRow = (addons: ConsignmentEntry[], requiredAddon: boolean, state: string, country: string) => {
  return addons.map((addon, index) => {
    const product = (
      <Fragment key={index}>
        <FlexboxContainerComponent flexDirection='column' padding='0px 0px 0px 125px'>
          {
            index === 0 &&
            <TableData fontWeight="bold">{requiredAddon ? "Product add-ons" : "Optional add-ons"}</TableData>
          }
          <FlexboxContainerComponent justifyContent='flex-start' margin={index === 0 ? '12px 0px 0px 0px' : '0px'} padding='0px 0 0'>
            <TableData deleted={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT} textDecoration={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT ? 'line-through' : 'normal'}>{addon.productName}</TableData>
          </FlexboxContainerComponent>

          <FlexboxContainerComponent flexDirection='column' gap='6px'>
            <FlexboxContainerComponent>
              <TableColumn>SKU</TableColumn>
              <TableColumn color={Text.primary}>{addon.sku}</TableColumn>
            </FlexboxContainerComponent>
          </FlexboxContainerComponent>
          <FlexboxContainerComponent>
            <TableColumn>Unit</TableColumn>
            <TableColumn color={Text.primary}>{addon.unitOfMeasurement?.fullName}</TableColumn>
          </FlexboxContainerComponent>
        </FlexboxContainerComponent>
      </Fragment>
    );

    const quantity = (
      <Fragment key={index}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          <TableData deleted={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{addon.quantity}</TableData>
        </FlexboxContainerComponent>
      </Fragment>
    );

    const unitPrice = (
      <Fragment key={index}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          {
            addon.discountedPrice !== addon.basePrice
              ? <TableData color={Main.error} deleted={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, addon.discountedPrice)}</TableData>
              : <TableData deleted={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, addon.basePrice)}</TableData>
          }
        </FlexboxContainerComponent>
      </Fragment>
    );

    const subTotal = (
      <Fragment key={index}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          <TableData deleted={addon.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, addon.totalPrice)}</TableData>
        </FlexboxContainerComponent>
      </Fragment>
    );

    return { product, quantity, unitPrice, subTotal, isRequiredAddon: requiredAddon, isOptionalAddon: !requiredAddon }
  })
}

export const rowStyle: any = (data: any) => {
  return {
    padding: `${Space.sp2x} ${Space.spBase} ${Space.sp2x} ${Space.sp2x}`,
    fontSize: FontSize.medium,
    border: 'none !important',
    lineHeight: '20px',
    backgroundColor: data.isRequiredAddon ? Background.light : data.isOptionalAddon ? Background.shaded : '#fff',
    verticalAlign: 'top'
  };
};

export const mapProductAddonPreviewItems = (entry: ConsignmentEntry) => {
  return {
    id: entry.id,
    entryNumber: entry.entryNumber!,
    parentEntryNumber: entry.parentEntryNumber!,
    productId: entry.productId!,
    productSKU: entry.productSKU!,
    productName: entry.productName!,
    quantity: entry.quantity!,
    basePrice: entry.basePrice!,
    totalPrice: entry.totalPrice!,
    plannedUsage: entry.plannedUsage!,
    requiredAddon: entry.requiredAddon!,
    serialNumber: entry.serialNumber!,
    unit: {
      id: entry.unitOfMeasurement?.id,
      code: entry.unitOfMeasurement?.fullName!,
    },
    addonId: entry.addonId
  }
}

export const getGroupedConsignmentEntries = (consignmentEntries: ConsignmentEntry[]) => {
  const groupedEntriesMap = new Map();

  const totalEntries = [...consignmentEntries]

  totalEntries.forEach(entry => {
    const key = entry.parentEntryNumber ?? entry.entryNumber;

    if (!groupedEntriesMap.has(key)) {
      groupedEntriesMap.set(key, []);
    }

    const mappedEntry = mapProductAddonPreviewItems(entry);

    groupedEntriesMap.get(key).push(mappedEntry);
  });

  return Array.from(groupedEntriesMap.values());
}

export const isPricingUpdatingFromHybris = (state: string, previousState: string) => {
  const priceLoading = state === MODIFIED_BY_VENDOR && PRICE_PENDING_PREVIOUS_STATES.includes(previousState) ? true : false;
  return priceLoading;
}

export const getConsignmentOnHoldReasonHeader = (consignmentDetailsResponse: ConsignmentDetails) => {
  if (!consignmentDetailsResponse.businessUnitCompany?.creditId) {
    return CREDIT_DESCRIPTIONS.AUTOMATION_FOUND_NO_CREDIT;
  } else if (consignmentDetailsResponse.businessUnitCompany?.creditDetails?.source === CREDIT_SOURCES.MDM &&
    consignmentDetailsResponse.businessUnitCompany?.creditDetails?.companyMdmDetails?.idStatus?.toLowerCase() === CREDIT_SOURCE_ACTIVITY_STATUS.INACTIVE) {
    return CREDIT_DESCRIPTIONS.EXTERNAL_SYSEM_INACTIVE;
  } else {
    return CREDIT_DESCRIPTIONS.AUTOMATION_FOUND_INSUFFICIENT_CREDIT;
  }
}

export const getConsignmentOnHoldReasonText = (consignmentDetailsResponse: ConsignmentDetails) => {
  if (!consignmentDetailsResponse.businessUnitCompany?.creditId) {
    return CREDIT_DESCRIPTIONS.SETUP_REQUIRED;
  } else if (consignmentDetailsResponse.businessUnitCompany?.creditDetails?.source === CREDIT_SOURCES.MDM &&
    consignmentDetailsResponse.businessUnitCompany?.creditDetails?.companyMdmDetails?.idStatus?.toLowerCase() === CREDIT_SOURCE_ACTIVITY_STATUS.INACTIVE) {
    return CREDIT_DESCRIPTIONS.EXTERNAL_SYSEM_INACTIVE_DESCRIPTION;
  } else {
    return ''
  }
}

/**
 * Checks if any entry in the consignment requires post-order shipping cost
 * by matching consignment entries with fulfillment request entries using SKU
 * @param consignmentEntries - Entries from the split consignment
 * @param fulfillmentRequest - Original fulfillment request entries with postOrderShippingCost flag
 * @returns boolean - true if any entry requires post-order shipping cost
 */
export const hasPostOrderShippingCostEntries = (
  consignmentEntries: any[],
  fulfillmentRequest: ConsignmentEntry[] | undefined
): boolean => {
  return consignmentEntries?.some(entry => {
    const matchingFulfillmentEntry = fulfillmentRequest?.find(
      (fr: ConsignmentEntry) => fr.sku === entry.productSKU
    );
    return matchingFulfillmentEntry?.postOrderShippingCost === true;
  }) ?? false;
}

/**
 * Whether a fulfilment split puts stock and manufacture on the same estimated day, which
 * order details asks the seller to confirm. Only a split that really mixes the two qualifies.
 * A row left on its default date carries no committed/manufacture date of its own, so the
 * date compared is the one the submit sends, falling back to preliminaryDeliveryDate.
 * @param fulfillmentRequest - The fulfilment form rows
 * @returns boolean - true if the first stock row and first manufacture row share a UTC day
 */
export const hasSameStockAndManufactureDate = (fulfillmentRequest: ConsignmentEntry[]): boolean => {
  const liveEntries = fulfillmentRequest.filter(entry => !entry.isDeleted);
  const stockEntry = liveEntries.find(entry => entry.committedQuantity > 0);
  const manufactureEntry = liveEntries.find(entry => entry.manufactureQuantity > 0);

  if (!stockEntry || !manufactureEntry) return false;

  return toUTCDay(stockEntry.committedDate || stockEntry.preliminaryDeliveryDate)
    === toUTCDay(manufactureEntry.manufactureDate || manufactureEntry.preliminaryDeliveryDate);
}
